import './Drawer.scss';

import { IconClose, IconWindowCollapse, IconWindowExpand } from '@hulu-react-style-components/icons';
import React, { forwardRef, useRef } from 'react';

import bem from '../../utils/bem';
import type { DrawerProps } from './useDrawerProps';

type DrawerRenderProp<T extends {}> =
  | ((props: {
      data?: T;
      isExpanded?: boolean;
      onClose?: () => void;
      drawerRef?: React.RefObject<HTMLElement>;
    }) => React.ReactNode)
  | string
  | null;

export interface DrawerPropsWithRenderProps<T extends {}> extends DrawerProps<T> {
  className?: string;
  content?: DrawerRenderProp<T>;
  title?: DrawerRenderProp<T>;
  isExpanded: boolean;
  size?: 'normal' | 'large';
  onToggleExpansion: () => void;
  emptyHeader?: boolean;
  openFrom?: 'left' | 'right';
  closeOnOutsideClick?: boolean;
}

const [block, element] = bem('drawer');

function withRenderProp<T extends {}>(
  prop?: DrawerRenderProp<T>,
  data?: T,
  isExpanded?: boolean,
  onClose?: () => void,
  drawerRef?: React.RefObject<HTMLElement>
): React.ReactNode {
  if (typeof prop === 'function') return prop({ data, isExpanded, onClose, drawerRef });
  return prop ?? null;
}

function DrawerInner<T extends {}>(
  {
    className,
    content,
    data,
    onClose,
    isOpen,
    title,
    isExpanded,
    size = 'normal',
    onToggleExpansion,
    emptyHeader,
    openFrom = 'right',
    closeOnOutsideClick = false,
  }: DrawerPropsWithRenderProps<T>,
  ref: React.ForwardedRef<HTMLElement>
): JSX.Element {
  const internalRef = useRef<HTMLElement>(null);
  const drawerRef = (ref as React.RefObject<HTMLElement>) || internalRef;

  const openClass = isOpen ? 'opened' : 'closed';
  const expandedClass = isOpen && isExpanded ? `expanded` : '';
  const sizeClass = size === 'large' ? 'large' : '';
  const openFromClass = openFrom === 'left' ? 'left' : '';

  const mergedClassName = `${block(openClass)} ${block(sizeClass)} ${block(expandedClass)} ${block(
    openFromClass
  )} ${className}`;

  return (
    <>
      {isOpen && closeOnOutsideClick && (
        <div className="drawer-backdrop" onClick={onClose} data-testid="drawer-backdrop" aria-hidden="true" />
      )}
      <aside ref={drawerRef} className={mergedClassName} aria-label="drawer" aria-hidden={!isOpen}>
        {isOpen && (
          <>
            {!emptyHeader && (
              <header data-testid="drawerHeader">
                <div className={element('title')}>{withRenderProp<T>(title, data)}</div>
                <div className={element('header-btns')}>
                  <button onClick={onToggleExpansion} aria-label="expand drawer">
                    {isExpanded ? (
                      <IconWindowCollapse width="20px" height="20px" title="Collapse" />
                    ) : (
                      <IconWindowExpand width="20px" height="20px" title="Expand" />
                    )}
                  </button>
                  <button onClick={onClose} aria-label="close drawer">
                    <IconClose width="20px" height="20px" title="Close" />
                  </button>
                </div>
              </header>
            )}
            <main>{withRenderProp<T>(content, data, isExpanded, onClose, drawerRef)}</main>
          </>
        )}
      </aside>
    </>
  );
}

const Drawer = forwardRef(DrawerInner) as <T extends {}>(
  props: DrawerPropsWithRenderProps<T> & { ref?: React.ForwardedRef<HTMLElement> }
) => JSX.Element;

export default Drawer;
